import { useApi } from 'contexts/Api/Api';
import { resolveAuthApiBaseUrl } from 'helpers/authApi';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import AuthModalButton from '../AuthModalButton/AuthModalButton';
import {
    AuthSwitchButton,
    AuthSwitchLink,
    Backdrop,
    CheckboxWrapper,
    CloseButton,
    Container,
    Content,
    FieldsGroup,
    Form,
    Loader,
    LoaderCircle,
    ModalDivider,
    Notification,
    OAuthButton,
    OAuthIconWrap,
    PrimaryButton,
    Row,
    Section,
    SeparatorLine,
    Text,
    TextField,
    Title,
} from './AuthModal.styled';

function GoogleIcon() {
    return (
        <OAuthIconWrap aria-hidden>
            <svg height="20" viewBox="0 0 18 18" width="20">
                <path
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.2 1.12-.84 2.06-1.8 2.7v2.24h2.92c1.72-1.58 2.68-3.92 2.68-6.58z"
                    fill="#4285F4"
                />
                <path
                    d="M9 18c2.44 0 4.5-.8 6-2.22l-2.92-2.24c-.8.54-1.82.86-3.08.86-2.36 0-4.36-1.58-5.08-3.72H.92v2.3A9 9 0 0 0 9 18z"
                    fill="#34A853"
                />
                <path
                    d="M3.92 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.14.28-1.68V5.02H.92A9 9 0 0 0 0 9c0 1.44.34 2.8.92 3.98l3-2.3z"
                    fill="#FBBC05"
                />
                <path
                    d="M9 3.58c1.34 0 2.54.46 3.48 1.36l2.6-2.6C13.5.9 11.44 0 9 0A9 9 0 0 0 .92 5.02l3 2.3C4.64 5.18 6.64 3.58 9 3.58z"
                    fill="#EA4335"
                />
            </svg>
        </OAuthIconWrap>
    );
}

export default function AuthModal({ screen = 'login', onClose }: AuthModalProps) {
    const [errors, setErrors] = useState<Record<string, string> | null>(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
    const [show, setShow] = useState<AuthModalScreen>(screen);
    const [loginEmail, setLoginEmail] = useState('');
    const api = useApi();

    // Standardized password reset: the login view links OUT to orca-front's
    // dedicated reset page (known-good) instead of driving the inline recovery
    // flow. Host-aware so staging links to staging and prod links to prod,
    // mirroring resolveAuthApiBaseUrl's host convention.
    const forgotPasswordBase =
        typeof window !== 'undefined' && window.location.hostname === 'staging.kromatic.com'
            ? 'https://staging.kromatic.com'
            : 'https://kromatic.com';
    const forgotPasswordHref = `${forgotPasswordBase}/auth/forgot-password${
        loginEmail ? `?email=${encodeURIComponent(loginEmail)}` : ''
    }`;

    const handleClose = useCallback(() => {
        if (!loading) {
            onClose();
        }
    }, [loading, onClose]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        },
        [handleClose]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        if (notification) {
            const timeout = setTimeout(() => setNotification(false), 5000);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [notification]);

    useEffect(() => {
        // Reset on screen change
        setErrors(null);
        setLoginEmail('');
        if (show !== 'recoveryVerify') {
            setRecoveryEmail(null);
        }
    }, [show]);

    async function handleRecoveryResend() {
        // Make request
        const response = await request('recoverySend', {
            email: recoveryEmail,
        });

        // Show notification
        if (response) {
            setNotification(true);
        }
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // Get form input data
        const formData = new FormData(e.currentTarget);
        const values: any = Object.fromEntries(formData.entries());
        if (show.startsWith('registration')) {
            values.email_confirm = values.email_confirm || values.email;
            values.password_confirm = values.password_confirm || values.password;
        }

        // Make request
        const response = await request(show, values);
        if (!response) {
            return;
        }

        // Check shown screen
        if (show === 'recoverySend') {
            setNotification(true);
            setRecoveryEmail(values.email);
            setShow('recoveryVerify');
        } else if (show === 'recoveryVerify') {
            setShow('recoverySuccess');
        }
    }

    async function request(currentScreen: AuthModalScreen, values: any) {
        if (loading) {
            return;
        }

        // Initialize
        setErrors(null);
        setLoading(true);

        // Make request
        let response;
        if (currentScreen === 'login') {
            response = await api.login(values);
        } else if (currentScreen === 'recoverySend') {
            response = await api.post('auth/recovery-send', null, values);
        } else if (currentScreen === 'recoveryVerify') {
            response = await api.post('auth/recovery-verify', null, {
                ...values,
                email: recoveryEmail,
            });
        } else if (currentScreen.startsWith('registration')) {
            response = await api.registration(values);
        }

        // Finish
        setLoading(false);
        if (response?.errors) {
            setErrors(response.errors);
        }
        return !response || response.errors ? null : response;
    }

    function handleGoogleOAuth(source: 'login' | 'registration') {
        if (loading) {
            return;
        }

        setErrors(null);
        const authApiBaseUrl = resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL);
        if (!authApiBaseUrl) {
            setErrors({
                detail: 'OAuth is unavailable right now. Please continue with email/password.',
            });
            return;
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${authApiBaseUrl}/auth/login/google`;
        form.style.display = 'none';

        const addField = (name: string, value: string) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        };

        const returnUrl = `${window.location.pathname}${window.location.search}` || '/';
        const subscribeToNews =
            source === 'registration'
                ? Boolean((document.getElementById('subscribe_to_news') as HTMLInputElement | null)?.checked)
                : false;

        addField('return_url', returnUrl.startsWith('/') ? returnUrl : '/');
        addField('subscribe_to_news', String(subscribeToNews));

        document.body.appendChild(form);
        form.submit();
    }

    return (
        <Container>
            <Content>
                <CloseButton
                    aria-label="Close modal"
                    data-testid="auth-close"
                    disabled={loading}
                    onClick={handleClose}
                    type="button"
                >
                    <svg aria-hidden fill="currentColor" width="18" height="18" viewBox="0 0 24 24">
                        <path d="M18.3 5.71a.9959.9959 0 0 0-1.41 0L12 10.59 7.11 5.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                    </svg>
                </CloseButton>

                {loading && (
                    <Loader width="40" height="40" viewBox="22 22 44 44">
                        <LoaderCircle cx="44" cy="44" r="20" />
                    </Loader>
                )}

                <Form
                    $loading={loading}
                    data-testid={
                        show === 'login'
                            ? 'auth-login-form'
                            : show.startsWith('registration')
                              ? 'auth-registration-form'
                              : undefined
                    }
                    onChange={() => setErrors(null)}
                    onSubmit={handleSubmit}
                >
                    {show === 'login' && (
                        <>
                            <Section $center $gap={16} $mb={20} $mt={20}>
                                <Title>Log in</Title>
                                <OAuthButton
                                    aria-label="Log in with Google"
                                    data-testid="auth-login-with-google"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('login')}
                                >
                                    <GoogleIcon />
                                    Log in with Google
                                </OAuthButton>
                                <ModalDivider>or use your account</ModalDivider>
                            </Section>
                            <FieldsGroup>
                                <TextField
                                    aria-label="Work email"
                                    data-testid="auth-email-input"
                                    disabled={loading}
                                    name="email"
                                    onChange={e => setLoginEmail(e.currentTarget.value)}
                                    placeholder="Work email"
                                    type="email"
                                />
                                {errors?.email && <Text $error>{errors.email}</Text>}
                                <TextField
                                    aria-label="Password"
                                    data-testid="auth-password-input"
                                    disabled={loading}
                                    name="password"
                                    placeholder="Password"
                                    type="password"
                                />
                                {errors?.password && <Text $error>{errors.password}</Text>}
                                {errors?.detail && <Text $error>{errors.detail}</Text>}
                            </FieldsGroup>
                            <Section $center $direction="row" $gap={20} $mb={36}>
                                <CheckboxWrapper $compact>
                                    <input
                                        disabled={loading}
                                        id="remember_me"
                                        name="remember_me"
                                        type="checkbox"
                                        aria-label="Remember me"
                                    />
                                    <label htmlFor="remember_me">Remember me</label>
                                </CheckboxWrapper>
                                <AuthSwitchLink
                                    aria-disabled={loading || undefined}
                                    aria-label="Forgot your password?"
                                    data-testid="auth-forgot-password"
                                    href={forgotPasswordHref}
                                    tabIndex={loading ? -1 : undefined}
                                >
                                    Forgot Password?
                                </AuthSwitchLink>
                            </Section>
                            <Row>
                                <PrimaryButton
                                    $tone="yellow"
                                    aria-label="Log in"
                                    data-testid="auth-login-submit"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Log in
                                </PrimaryButton>
                            </Row>
                            <SeparatorLine />
                            <Section $center $gap={16}>
                                <Text $center $size={18}>
                                    Not a member yet?
                                </Text>
                                    <AuthModalButton
                                        aria-label="Join now"
                                        data-testid="auth-join-now"
                                        disabled={loading}
                                        screen={screen === 'registrationPremium' ? 'registrationPremium' : 'registration'}
                                        onClick={setShow}
                                    >
                                        Join now
                                    </AuthModalButton>
                            </Section>
                        </>
                    )}

                    {show === 'recoverySend' && (
                        <>
                            <Title>
                                Forgot your
                                <br />
                                password?
                            </Title>
                            <Row>
                                <Text $size={12}>Please enter the email you use to sign in.</Text>
                            </Row>
                            <TextField
                                aria-label="Email"
                                data-testid="auth-recovery-email"
                                disabled={loading}
                                name="email"
                                placeholder="Email"
                                type="email"
                            />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <AuthModalButton
                                    aria-label="Request password reset"
                                    data-testid="auth-request-reset"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Request password reset
                                </AuthModalButton>
                            </Row>
                            <Row>
                                <AuthSwitchButton
                                    aria-label="Back to Sign in"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => setShow('login')}
                                >
                                    Back to Sign in
                                </AuthSwitchButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoverySuccess' && (
                        <>
                            <Title>Password Changed!</Title>
                            <Row>
                                <Text $size={12}>Your password has been changed successfully.</Text>
                            </Row>
                            <Row>
                                <AuthModalButton
                                    aria-label="Continue to Sign in"
                                    data-testid="auth-continue-to-sign-in"
                                    disabled={loading}
                                    screen="login"
                                    onClick={setShow}
                                >
                                    Continue to Sign in
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoveryVerify' && recoveryEmail && (
                        <>
                            <Title>Set new password</Title>
                            <Text $size={12}>
                                You have requested a password reset. To get a new password, please check your Inbox the
                                email from Kromatic and enter the Password reset code we have sent to: <b>{recoveryEmail}</b>
                            </Text>
                            <Row>
                                <AuthSwitchButton
                                    aria-label="Change email"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => setShow('recoverySend')}
                                >
                                    Change email
                                </AuthSwitchButton>
                                <AuthSwitchButton
                                    aria-label="Resend code"
                                    data-testid="auth-resend-code"
                                    disabled={loading}
                                    type="button"
                                    onClick={handleRecoveryResend}
                                >
                                    Resend code
                                </AuthSwitchButton>
                            </Row>
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            <TextField
                                aria-label="Password reset code"
                                data-testid="auth-reset-code-input"
                                disabled={loading}
                                maxLength={12}
                                minLength={12}
                                name="code"
                                placeholder="Password reset code"
                            />
                            {errors?.code && <Text $error>{errors.code}</Text>}
                            <TextField
                                aria-label="New password (at least 8 characters)"
                                data-testid="auth-new-password-input"
                                name="new_password"
                                placeholder="New password (at least 8 characters)"
                                type="password"
                            />
                            {errors?.new_password && <Text $error>{errors.new_password}</Text>}
                            <TextField
                                aria-label="Confirm new password"
                                data-testid="auth-confirm-password-input"
                                disabled={loading}
                                name="new_password_confirm"
                                placeholder="Confirm new password"
                                type="password"
                            />
                            {errors?.new_password_confirm && <Text $error>{errors.new_password_confirm}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <AuthModalButton
                                    aria-label="Set new password"
                                    data-testid="auth-set-password"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Set new password
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show.startsWith('registration') && (
                        <>
                            <Section $center $gap={16} $mb={20} $mt={20}>
                                <Title>Join for free</Title>
                                <Text $center $size={18}>
                                    Please create an account
                                    <br />
                                    to access Kromatic
                                </Text>
                                <Section $center $direction="row" $gap={8}>
                                    <Text $size={14}>Already have an account?</Text>
                                    <AuthSwitchButton
                                        aria-label="Log in"
                                        disabled={loading}
                                        type="button"
                                        onClick={() => setShow('login')}
                                    >
                                        Log in
                                    </AuthSwitchButton>
                                </Section>
                            </Section>
                            <Section $center $gap={16} $mb={20}>
                                <OAuthButton
                                    aria-label="Sign up with Google"
                                    data-testid="auth-signup-with-google"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('registration')}
                                >
                                    <GoogleIcon />
                                    Sign up with Google
                                </OAuthButton>
                                <ModalDivider>or use your account</ModalDivider>
                            </Section>
                            <FieldsGroup>
                                <TextField
                                    aria-label="Registration name"
                                    data-testid="auth-registration-name"
                                    disabled={loading}
                                    name="name"
                                    minLength={2}
                                    placeholder="Your name"
                                    type="text"
                                />
                                {errors?.name && <Text $error>{errors.name}</Text>}
                                <TextField
                                    aria-label="Registration email"
                                    data-testid="auth-registration-email"
                                    disabled={loading}
                                    name="email"
                                    placeholder="Work email"
                                    type="email"
                                />
                                {errors?.email && <Text $error>{errors.email}</Text>}
                                <TextField
                                    aria-label="Registration password"
                                    data-testid="auth-registration-password"
                                    disabled={loading}
                                    name="password"
                                    placeholder="Password"
                                    type="password"
                                />
                                {errors?.password && <Text $error>{errors.password}</Text>}
                                {errors?.detail && <Text $error>{errors.detail}</Text>}
                            </FieldsGroup>
                            <Section $mb={20}>
                                <CheckboxWrapper>
                                    <input
                                        disabled={loading}
                                        defaultChecked
                                        id="subscribe_to_news"
                                        name="subscribe_to_news"
                                        type="checkbox"
                                    />
                                    <label htmlFor="subscribe_to_news">Subscribe to Kromatic Newsletters</label>
                                </CheckboxWrapper>
                            </Section>
                            <Row>
                                <PrimaryButton
                                    $tone="yellow"
                                    aria-label="Submit registration"
                                    data-testid="auth-registration-submit"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Join now
                                </PrimaryButton>
                            </Row>
                        </>
                    )}
                </Form>
            </Content>

            <Notification $show={notification} data-testid="auth-check-email-notification" role="status">
                Check your email
            </Notification>

            <Backdrop onClick={() => !loading && handleClose()} />
        </Container>
    );
}
